
import { useState, useCallback } from 'react';
import { universalStorage } from '@/services/storage/universalStorage';
import { v4 as uuidv4 } from 'uuid';

// Update AdminAction to match the expected interface
export interface AdminAction {
  id: string;
  entityId: string;
  entityType: 'student' | 'teacher' | 'admin' | 'system';
  actionType: 'create' | 'update' | 'delete' | 'view' | 'download' | 'upload' | 'approve' | 'reject';
  performedBy: string;
  timestamp: string;
  description: string;
  details: Record<string, any>; // Added missing details property
  metadata?: Record<string, any>;
}

export const useAdminActions = (entityId: string) => {
  const [actions, setActions] = useState<AdminAction[]>([]);

  const refreshActions = useCallback(async () => {
    try {
      const allActions = await universalStorage.getItem<AdminAction[]>('nalc_admin_actions') || [];
      const entityActions = allActions.filter(action => action.entityId === entityId);
      setActions(entityActions);
    } catch (error) {
      console.error('Failed to fetch admin actions:', error);
      setActions([]);
    }
  }, [entityId]);

  const addAction = useCallback(async (actionData: Omit<AdminAction, 'id' | 'timestamp'>) => {
    try {
      const newAction: AdminAction = {
        ...actionData,
        id: uuidv4(),
        timestamp: new Date().toISOString()
      };

      const allActions = await universalStorage.getItem<AdminAction[]>('nalc_admin_actions') || [];
      allActions.push(newAction);
      
      await universalStorage.setItem('nalc_admin_actions', allActions);
      await refreshActions();
      
      return newAction;
    } catch (error) {
      console.error('Failed to add admin action:', error);
      throw error;
    }
  }, [refreshActions]);

  const logStudentModification = useCallback((
    studentId: number,
    modifiedBy: string,
    changes: Record<string, any>,
    description: string
  ) => {
    return addAction({
      entityId: studentId.toString(),
      entityType: 'student',
      actionType: 'update',
      performedBy: modifiedBy,
      description: description,
      details: { changes }, // Use details instead of metadata for changes
      metadata: { changes }
    });
  }, [addAction]);

  const logDocumentAction = useCallback((
    documentId: string,
    status: 'uploaded' | 'approved' | 'rejected',
    handledBy: string,
    actionDetails: Record<string, any>
  ) => {
    const actionType = status === 'uploaded' ? 'upload' : status === 'approved' ? 'approve' : 'reject';
    return addAction({
      entityId: documentId,
      entityType: 'system',
      actionType: actionType as any,
      performedBy: handledBy,
      description: `Document ${status}`,
      details: actionDetails, // Use details instead of metadata
      metadata: actionDetails
    });
  }, [addAction]);

  const logPermissionChange = useCallback((
    userId: string,
    changedBy: string,
    oldPermissions: string[],
    newPermissions: string[]
  ) => {
    return addAction({
      entityId: userId,
      entityType: 'admin',
      actionType: 'update',
      performedBy: changedBy,
      description: 'Permission changed',
      details: { oldPermissions, newPermissions }, // Use details instead of metadata
      metadata: { oldPermissions, newPermissions }
    });
  }, [addAction]);

  const logSystemSettingChange = useCallback((
    settingKey: string,
    changedBy: string,
    oldValue: any,
    newValue: any
  ) => {
    return addAction({
      entityId: settingKey,
      entityType: 'system',
      actionType: 'update',
      performedBy: changedBy,
      description: 'System setting changed',
      details: { oldValue, newValue }, // Use details instead of metadata
      metadata: { oldValue, newValue }
    });
  }, [addAction]);

  return {
    actions,
    refreshActions,
    logStudentModification,
    logDocumentAction,
    logPermissionChange,
    logSystemSettingChange
  };
};
